import { __rest, __assign } from 'tslib';
import React, { forwardRef, useRef, useCallback, useEffect } from 'react';
import mergeRefs from 'react-merge-refs';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { Popover } from '../../../../popover/esm';
import { Portal } from '../../../../portal/esm';
import { Stack } from '../../../../stack/esm';
import { usePrevious, useClickOutside } from '@alfalab/hooks';
import { stackingOrder } from '@alfalab/stack-context';

var styles = {"toastPlate":"toast__toastPlate_kjbim","title":"toast__title_kjbim","popoverInner":"toast__popoverInner_kjbim","block":"toast__block_kjbim","fixed":"toast__fixed_kjbim","enter":"toast__enter_kjbim","enterActive":"toast__enterActive_kjbim","exit":"toast__exit_kjbim","exitActive":"toast__exitActive_kjbim"};
require('./index.css');

var CSS_TRANSITION_CLASS_NAMES = {
    enter: styles.enter,
    enterActive: styles.enterActive,
    exit: styles.exit,
    exitActive: styles.exitActive,
};
var noop = function () { };
var BaseToast = forwardRef(function (_a, ref) {
    var _b;
    var anchorElement = _a.anchorElement, position = _a.position, offset = _a.offset, open = _a.open, _c = _a.autoCloseDelay, autoCloseDelay = _c === void 0 ? 3000 : _c, className = _a.className, titleClassName = _a.titleClassName, bottomOffset = _a.bottomOffset, _d = _a.style, style = _d === void 0 ? {} : _d, block = _a.block, _e = _a.zIndex, zIndex = _e === void 0 ? stackingOrder.TOAST : _e, ToastPlate = _a.ToastPlate, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onTouchStart = _a.onTouchStart, onClose = _a.onClose, getPortalContainer = _a.getPortalContainer, useAnchorWidth = _a.useAnchorWidth, _f = _a.closeWithClickOutside, closeWithClickOutside = _f === void 0 ? true : _f, fallbackPlacements = _a.fallbackPlacements, restProps = __rest(_a, ["anchorElement", "position", "offset", "open", "autoCloseDelay", "className", "titleClassName", "bottomOffset", "style", "block", "zIndex", "ToastPlate", "onMouseEnter", "onMouseLeave", "onTouchStart", "onClose", "getPortalContainer", "useAnchorWidth", "closeWithClickOutside", "fallbackPlacements"]);
    var plateRef = useRef(null);
    var timerId = useRef(0);
    var prevOpen = usePrevious(open);
    var startTimer = useCallback(function () {
        clearTimeout(timerId.current);
        timerId.current = window.setTimeout(onClose, autoCloseDelay);
    }, [autoCloseDelay, onClose]);
    var stopTimer = useCallback(function () {
        clearTimeout(timerId.current);
    }, []);
    var handleMouseEnter = useCallback(function (event) {
        stopTimer();
        if (onMouseEnter) {
            onMouseEnter(event);
        }
    }, [onMouseEnter, stopTimer]);
    var handleMouseLeave = useCallback(function (event) {
        startTimer();
        if (onMouseLeave) {
            onMouseLeave(event);
        }
    }, [onMouseLeave, startTimer]);
    var handleTouchStart = useCallback(function (event) {
        stopTimer();
        if (onTouchStart) {
            onTouchStart(event);
        }
    }, [onTouchStart, stopTimer]);
    var handleClickOutside = useCallback(function () {
        onClose();
        stopTimer();
    }, [onClose, stopTimer]);
    useClickOutside(plateRef, closeWithClickOutside ? handleClickOutside : noop);
    useEffect(function () { return function () { return clearTimeout(timerId.current); }; }, []);
    useEffect(function () {
        if (open !== prevOpen && open) {
            startTimer();
        }
    }, [open, prevOpen, startTimer, stopTimer]);
    var props = {
        block: block,
        titleClassName: cn(titleClassName, styles.title),
        onClose: onClose,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onTouchStart: handleTouchStart,
        ref: mergeRefs([ref, plateRef]),
    };
    if (anchorElement) {
        return (React.createElement(Popover, { open: open, anchorElement: anchorElement, position: position, offset: offset, popperClassName: styles.popoverInner, className: cn((_b = {}, _b[styles.block] = block, _b)), getPortalContainer: getPortalContainer, zIndex: zIndex, useAnchorWidth: useAnchorWidth, fallbackPlacements: fallbackPlacements }, ToastPlate ? (React.createElement(ToastPlate, __assign({}, restProps, { style: style, className: className }, props))) : null));
    }
    return (React.createElement(Stack, { value: zIndex }, function (computedZIndex) { return (React.createElement(Portal, { getPortalContainer: getPortalContainer },
        React.createElement(CSSTransition, { unmountOnExit: true, in: open, timeout: 150, nodeRef: plateRef, classNames: CSS_TRANSITION_CLASS_NAMES }, ToastPlate ? (React.createElement(ToastPlate, __assign({}, restProps, { className: cn(styles.fixed, styles.toastPlate, className), style: __assign(__assign({}, style), { bottom: bottomOffset && "".concat(bottomOffset, "px"), zIndex: computedZIndex }) }, props))) : null))); }));
});

export { BaseToast };
